import routes from "routes/routes";

/**
 * Invite deep link handling (#1902, link built by #1900).
 *
 * The mail links to `/account/login?email=<address>&invite=<organizationId>`.
 * Neither parameter is a credential: the invite is keyed on the address and
 * only becomes a membership once the recipient proves the mailbox via OTP, so
 * forwarding the link gains nobody anything. `email` is a prefill; `invite`
 * exists purely so the post-login landing can confirm *that* organization by
 * name instead of dropping an invited person on a generic profile page.
 */

/** UUID shape — anything else is treated as no invite at all. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const readInviteOrganizationId = (search: string): string | null => {
  const raw = new URLSearchParams(search).get("invite");
  return raw && UUID.test(raw) ? raw : null;
};

/** The invited address, used only as the initial value of the email field. */
export const readInviteEmail = (search: string): string | null => {
  const raw = new URLSearchParams(search).get("email");
  if (!raw) return null;
  const trimmed = raw.trim();
  // Cheap sanity check only — the field validates properly on submit, and a
  // junk value must not be silently swallowed either.
  return trimmed.length > 0 && trimmed.length <= 254 ? trimmed : null;
};

/**
 * Where an invited person lands after the OTP step: the account page, told
 * which membership to confirm. Used as the flow's `returnTo`, so it goes
 * through the same open-redirect sanitizer as every other return target.
 */
export const buildInviteReturnTo = (organizationId: string): string =>
  `${routes.accountPath}?invited=${encodeURIComponent(organizationId)}`;

/** Reads the confirmed-membership marker on the account page. */
export const readInvitedOrganizationId = (search: string): string | null => {
  const raw = new URLSearchParams(search).get("invited");
  return raw && UUID.test(raw) ? raw : null;
};
